import React, { useState, useEffect, useContext } from 'react';
import axios from '../../axiosConfig';
import { TrainerContext } from '../../context/TrainerContext';
import InviteTrainerModal from './InviteTrainerModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import {
    canRemoveTrainer,
    canInviteTrainers,
    getRoleDisplayName
} from '../../utils/trainerRoleUtils';
import '../../styles/TrainersStyles.css';

const TrainersList = () => {
    const { trainer } = useContext(TrainerContext);
    const [trainers, setTrainers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [trainerToDelete, setTrainerToDelete] = useState(null);

    useEffect(() => {
        if (!trainer?.schoolId) {
            setLoading(false);
            return;
        }
        const token = localStorage.getItem('jwt');

        axios
            .get(`/api/v1/trainer/find-all/${trainer.schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                setTrainers(res.data);
                setLoading(false);
            })
            .catch(() => {
                setError('Błąd podczas pobierania listy trenerów.');
                setLoading(false);
            });
    }, [trainer?.schoolId]);

    const formatAddress = (address) => {
        if (!address) return 'Brak danych';
        return `${address.street || ''} ${address.buildingNumber || ''}${
            address.apartmentNumber ? '/' + address.apartmentNumber : ''
        }, ${address.postalCode || ''} ${address.city || ''}, ${
            address.country || ''
        }`.trim();
    };

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const openDeleteModal = (trainerData) => {
        setTrainerToDelete(trainerData);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setTrainerToDelete(null);
    };

    const handleDeleteTrainer = async () => {
        if (!trainerToDelete) return;

        const token = localStorage.getItem('jwt');

        try {
            await axios.delete(`/api/v1/trainer/remove-from-school/${trainerToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setTrainers((prevTrainers) =>
                prevTrainers.filter((t) => t.id !== trainerToDelete.id)
            );

            closeDeleteModal();
        } catch (err) {
            console.error('Error deleting trainer:', err);
            setError('Błąd podczas usuwania trenera.');
            closeDeleteModal();
        }
    };

    return (
        <div className="trainers-page-container">
            <div className="trainers-header-container">
                {canInviteTrainers(trainer?.trainerSchoolRole) && (
                    <button
                        className="trainers-invite-button"
                        onClick={openModal}
                    >
                        Dodaj trenera
                    </button>
                )}
            </div>

            {loading ? (
                <p className="trainers-loading-text">Ładowanie...</p>
            ) : error ? (
                <p className="trainers-error-message">{error}</p>
            ) : (
                <>
                    <div className="trainers-table-container">
                        <table className="trainers-table">
                            <thead>
                            <tr>
                                <th>Imię i Nazwisko</th>
                                <th>Email</th>
                                <th>Telefon</th>
                                <th>Adres</th>
                                <th>Rola</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {trainers.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="trainers-no-data-row">Brak trenerów.</td>
                                </tr>
                            ) : (
                                trainers.map((t) => {
                                    const fullName =
                                        t.firstName && t.lastName
                                            ? `${t.firstName} ${t.lastName}`
                                            : t.username;

                                    const isCurrentUser = trainer?.id === t.id;
                                    const canRemove = !isCurrentUser && canRemoveTrainer(trainer?.trainerSchoolRole, t.trainerSchoolRole);

                                    return (
                                        <tr key={t.id} className="trainers-clickable-row">
                                            <td className="trainers-centered-column">
                                                <strong>{fullName}</strong>
                                                {isCurrentUser && <span className="trainers-you-badge">(Ty)</span>}
                                            </td>
                                            <td className="trainers-centered-column">{t.email || '---'}</td>
                                            <td className="trainers-centered-column">{t.phone || '---'}</td>
                                            <td className="trainers-centered-column">{formatAddress(t.address)}</td>
                                            <td className="trainers-centered-column">
                                                <span className={`trainers-role-badge trainers-role-${t.trainerSchoolRole?.toLowerCase()}`}>
                                                    {getRoleDisplayName(t.trainerSchoolRole)}
                                                </span>
                                            </td>
                                            <td className="trainers-centered-column">
                                                {canRemove && (
                                                    <button
                                                        className="trainers-delete-button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openDeleteModal(t);
                                                        }}
                                                    >
                                                        Usuń
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                        </table>
                    </div>

                    <div className="trainers-mobile-cards">
                        {trainers.length === 0 ? (
                            <div className="trainers-mobile-no-data">
                                Brak trenerów
                            </div>
                        ) : (
                            trainers.map((t) => {
                                const fullName =
                                    t.firstName && t.lastName
                                        ? `${t.firstName} ${t.lastName}`
                                        : t.username;

                                const isCurrentUser = trainer?.id === t.id;
                                const canRemove = !isCurrentUser && canRemoveTrainer(trainer?.trainerSchoolRole, t.trainerSchoolRole);

                                return (
                                    <div
                                        key={t.id}
                                        className="trainers-card"
                                    >
                                        <div className="trainers-card-header">
                                            <h3 className="trainers-name">
                                                {fullName}
                                                {isCurrentUser && <span className="trainers-you-badge">(Ty)</span>}
                                            </h3>
                                            <span className={`trainers-role-badge trainers-role-${t.trainerSchoolRole?.toLowerCase()}`}>
                                                {getRoleDisplayName(t.trainerSchoolRole)}
                                            </span>
                                        </div>
                                        <div className="trainers-details">
                                            <div className="trainers-detail-item">
                                                <span className="trainers-detail-label">Email</span>
                                                <span className="trainers-detail-value">{t.email || '---'}</span>
                                            </div>
                                            <div className="trainers-detail-item">
                                                <span className="trainers-detail-label">Telefon</span>
                                                <span className="trainers-detail-value">{t.phone || '---'}</span>
                                            </div>
                                            <div className="trainers-detail-item">
                                                <span className="trainers-detail-label">Adres</span>
                                                <span className="trainers-detail-value">{formatAddress(t.address)}</span>
                                            </div>
                                        </div>
                                        {canRemove && (
                                            <div className="trainers-card-actions">
                                                <button
                                                    className="trainers-delete-button-mobile"
                                                    onClick={() => openDeleteModal(t)}
                                                >
                                                    Usuń trenera
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </>
            )}

            <InviteTrainerModal isOpen={isModalOpen} onClose={closeModal} />

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={handleDeleteTrainer}
                itemName={trainerToDelete ?
                    (trainerToDelete.firstName && trainerToDelete.lastName
                        ? `${trainerToDelete.firstName} ${trainerToDelete.lastName}`
                        : trainerToDelete.username)
                    : ''
                }
            />
        </div>
    );
};

export default TrainersList;